import React from "react";
import { Box, Text, Group } from "@mantine/core";

import { useBranding } from "@/contexts/BrandingContext";

// ============================================
// Types
// ============================================

export type LogoSize = "small" | "medium" | "large" | "xlarge";

export interface LogoProps {
  /** Size variant */
  size?: LogoSize;
  /** Show company name next to logo */
  withName?: boolean;
  /** Show version number (e.g., "v1.0.7") */
  version?: string;

  /** Logo background color */
  bg?: string;
}

// ============================================
// Size Configuration
// ============================================

interface SizeConfig {
  height: number;
  fontSize: string;
  fontWeight: number;
  gap: "xs" | "sm" | "md";
  versionFontSize: string;
}

const SIZE_CONFIG: Record<LogoSize, SizeConfig> = {
  small: {
    height: 28,
    fontSize: "1.3rem",
    fontWeight: 600,
    gap: "sm",
    versionFontSize: "0.65rem",
  },
  medium: {
    height: 36,
    fontSize: "1.25rem",
    fontWeight: 600,
    gap: "sm",
    versionFontSize: "0.7rem",
  },
  large: {
    height: 48,
    fontSize: "1.5rem",
    fontWeight: 700,
    gap: "md",
    versionFontSize: "0.75rem",
  },
  xlarge: {
    height: 64,
    fontSize: "1.8rem",
    fontWeight: 700,
    gap: "md",
    versionFontSize: "0.75rem",
  },
};

// ============================================
// Component
// ============================================

export const Logo: React.FC<LogoProps> = ({
  size = "medium",
  withName = false,
  version,
  bg = "",
}) => {
  const { brand } = useBranding();
  const config = SIZE_CONFIG[size];

  const logoImage = (
    <Box
      component="img"
      src={brand.logo.mark}
      alt={brand.name}
      h={config.height}
      w="auto"
      style={{ objectFit: "contain", display: "block" }}
    />
  );

  if (!withName) {
    return (
      <Box bg={bg} p={bg ? 4 : 0} w="fit-content">
        {logoImage}
      </Box>
    );
  }

  return (
    <Group gap={config.gap} align="center" wrap="nowrap" bg={bg} p={bg ? 4 : 0}>
      {logoImage}
      <Group gap="sm">
        <Text
          fw={config.fontWeight}
          fz={config.fontSize}
          c="brand.6"
          lh={1.2}
          style={{
            letterSpacing: "-0.02em",
            fontFamily:
              "var(--mantine-font-family-headings, 'Space Grotesk', sans-serif)",
          }}
        >
          {brand.name}
        </Text>
        {version && (
          <Text fz={config.versionFontSize} c="neutral.4" lh={1} mt={2} opacity={0.65}>
            {version}
          </Text>
        )}
      </Group>
    </Group>
  );
};